

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Container, Grid, Card, Typography, LinearProgress, Divider, 
  Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, IconButton, Chip, Alert, Tooltip, Radio, RadioGroup, 
  FormControlLabel, FormControl, FormLabel
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningIcon from '@mui/icons-material/Warning';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Helper function to get phase badge properties
const getPhaseBadge = (phase) => {
  switch (phase) {
    case 'Phase1':
      return { label: 'Phase 1', color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.1)' };
    case 'Phase2':
      return { label: 'Phase 2', color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.1)' };
    case 'Funded':
      return { label: 'Funded', color: '#ffd700', bgColor: 'rgba(255, 215, 0, 0.1)' };
    case 'Failed':
      return { label: 'Failed', color: '#f44336', bgColor: 'rgba(244, 67, 54, 0.1)' };
    default:
      return { label: phase || 'Unknown', color: '#9e9e9e', bgColor: 'rgba(158, 158, 158, 0.1)' };
  }
};

// Helper function to get progress color based on percentage
const getProgressColor = (percent, limit) => {
  const ratio = percent / limit;
  if (ratio < 0.5) return '#10b981'; // Green
  if (ratio < 0.8) return '#f59e0b'; // Yellow
  return '#ef4444'; // Red
};

function TraderSummary() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    accountBalance: 0,
    currentEquity: 0,
    totalProfitLoss: 0,
    totalTrades: 0,
    openPositions: 0,
    pendingOrders: 0,
    closedTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    winRate: 0,
    bestTrade: 0,
    worstTrade: 0,
    dailyDrawdown: 0,
    totalDrawdown: 0,
    tradingDays: 0,
    recentTrades: []
  });

  // Challenge purchase states
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedChallengeForDetails, setSelectedChallengeForDetails] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('MT5');
  const [transactionId, setTransactionId] = useState('');
  const [paymentEmail, setPaymentEmail] = useState('');
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  
  // User challenges states
  const [userChallenges, setUserChallenges] = useState([]);
  const [editingChallengeId, setEditingChallengeId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const challenges = [
    {
      type: 'Pro',
      amount: 25000,
      icon: '🚀',
      color: '#3b82f6',
      paymentLink: 'https://rzp.io/rzp/J4D6qIdM',
      accessFee: 79,
      overview: 'The Pro Challenge is ideal for traders who want to demonstrate stable risk management with moderate targets. It is the perfect entry point to test your strategy under professional parameters.',
      details: [
        { label: 'Account Size', value: '$25,000 (Demo)' },
        { label: 'Profit Target (Phase 1)', value: '10%' },
        { label: 'Profit Target (Phase 2)', value: '5%' },
        { label: 'Maximum Overall Drawdown', value: '10%' },
        { label: 'Maximum Daily Drawdown', value: '5%' },
        { label: 'Minimum Trading Days', value: '3' },
        { label: 'Duration per Phase', value: 'Unlimited' },
        { label: 'Leverage', value: '1:100' },
        { label: 'Trading Style', value: 'Manual strategies only' }
      ]
    },
    {
      type: 'Elite',
      amount: 50000,
      icon: '🏆',
      color: '#f59e0b',
      paymentLink: 'https://rzp.io/rzp/WZDdzg0',
      accessFee: 134,
      overview: 'For experienced traders aiming to showcase advanced discipline and strategy execution. The Elite Challenge allows greater flexibility while maintaining balanced risk parameters.',
      details: [
        { label: 'Account Size', value: '$50,000 (Demo)' },
        { label: 'Profit Target (Phase 1)', value: '10%' },
        { label: 'Profit Target (Phase 2)', value: '5%' },
        { label: 'Maximum Overall Drawdown', value: '10%' },
        { label: 'Maximum Daily Drawdown', value: '5%' },
        { label: 'Minimum Trading Days', value: '3' },
        { label: 'Duration per Phase', value: 'Unlimited' },
        { label: 'Leverage', value: '1:100' },
        { label: 'Trading Style', value: 'Manual strategies only' }
      ]
    },
    {
      type: 'Master',
      amount: 100000,
      icon: '👑',
      color: '#8b5cf6',
      paymentLink: 'https://rzp.io/rzp/InvOs74F',
      accessFee: 256,
      overview: 'The Master Challenge is designed for seasoned traders ready to showcase consistent performance across larger evaluation parameters. It emphasizes patience, drawdown control, and sustainable profitability.',
      details: [
        { label: 'Account Size', value: '$100,000 (Demo)' },
        { label: 'Profit Target (Phase 1)', value: '10%' },
        { label: 'Profit Target (Phase 2)', value: '5%' },
        { label: 'Maximum Overall Drawdown', value: '10%' },
        { label: 'Maximum Daily Drawdown', value: '5%' },
        { label: 'Minimum Trading Days', value: '3' },
        { label: 'Duration per Phase', value: 'Unlimited' },
        { label: 'Leverage', value: '1:100' },
        { label: 'Trading Style', value: 'Manual strategies only' }
      ]
    }
  ];

  useEffect(() => {
    if (user?.email) {
      fetchSummaryData();
      fetchUserChallenges();
    }
  }, [user]);

  const fetchUserChallenges = async () => {
    try {
      // Use user-with-progress endpoint to get phase tracking data
      const response = await fetch(getApiUrl('/api/challenges/user-with-progress'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      if (data.success) {
        setUserChallenges(data.challenges || []);
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const handleBuyClick = (challenge) => {
    setSelectedChallenge(challenge);
    window.open(challenge.paymentLink, '_blank');
    setShowTransactionModal(true);
    setShowBuyModal(false);
  };

  const handleChallengeSelect = (challenge) => {
    setSelectedChallengeForDetails(challenge);
  };

  const handleTransactionSubmit = async () => {
    if (!transactionId || !paymentEmail) {
      alert('Please fill all fields');
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/challenges/purchase'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          challengeType: selectedChallenge.type,
          challengeAmount: selectedChallenge.amount,
          platform: selectedPlatform,
          transactionId,
          paymentEmail
        })
      });

      const data = await response.json();
      if (data.success) {
        setPurchaseSuccess(true);
        setTransactionId('');
        setPaymentEmail('');
        setTimeout(() => {
          setShowTransactionModal(false);
          setPurchaseSuccess(false);
          fetchUserChallenges();
        }, 3000);
      } else {
        alert(data.error || 'Failed to record purchase');
      }
    } catch (error) {
      console.error('Error submitting transaction:', error);
      alert('Failed to submit transaction');
    }
  };

  const handleRenameChallenge = async (challengeId) => {
    if (!editingName.trim()) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/challenges/${challengeId}/rename`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ customName: editingName })
      });

      const data = await response.json();
      if (data.success) {
        fetchUserChallenges();
        setEditingChallengeId(null);
        setEditingName('');
      } else {
        alert(data.error || 'Failed to rename challenge');
      }
    } catch (error) {
      console.error('Error renaming challenge:', error);
      alert('Failed to rename challenge');
    }
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      
      // Fetch comprehensive summary from new API endpoint
      const response = await fetch(getApiUrl('/api/summary'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email: user.email })
      });

      const data = await response.json();
      
      if (response.status === 401 || response.status === 403) {
        console.warn('Authentication failed');
        setLoading(false);
        return;
      }
      
      if (!data.summary) {
        console.warn('No summary data available');
        setLoading(false);
        return;
      }

      const summaryData = data.summary;
      
      // Map database fields to component state
      setSummary({
        accountBalance: summaryData.StartingBalance ?? 0,
        currentEquity: summaryData.CurrentEquity ?? 0,
        totalProfitLoss: summaryData.TotalProfitLoss || 0,
        totalTrades: summaryData.TotalTrades || 0,
        openPositions: summaryData.OpenPositions || 0,
        pendingOrders: summaryData.PendingOrders || 0,
        closedTrades: summaryData.TotalTrades || 0,
        winningTrades: summaryData.WinningTrades || 0,
        losingTrades: summaryData.LosingTrades || 0,
        winRate: summaryData.WinRate || 0,
        bestTrade: summaryData.BestTrade || 0,
        worstTrade: summaryData.WorstTrade || 0,
        dailyDrawdown: summaryData.DailyDrawdownPercent || 0,
        totalDrawdown: summaryData.TotalDrawdownPercent || 0,
        tradingDays: summaryData.TradingDays || 0,
        recentTrades: data.recentTrades || []
      });

      console.log('✅ Summary data loaded:', summaryData);
      
    } catch (error) {
      console.error('❌ Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ icon: Icon, title, value, subtitle, color, progress, maxValue }) => (
    <Card
      sx={{
        bgcolor: '#1e222d',
        border: '1px solid #2a2e39',
        borderRadius: 2,
        p: 3,
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${color}40`,
          borderColor: color
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${color}20, ${color}40)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            border: `2px solid ${color}60`
          }}
        >
          <Icon sx={{ color: color, fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
            {value}
          </Typography>
        </Box>
      </Box>
      {subtitle && (
        <Typography variant="body2" sx={{ color: '#d1d4dc', mt: 1 }}>
          {subtitle}
        </Typography>
      )}
      {progress !== undefined && maxValue && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(progress / maxValue) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#2a2e39',
              '& .MuiLinearProgress-bar': {
                bgcolor: progress > maxValue * 0.8 ? '#ef4444' : color,
                borderRadius: 4
              }
            }}
          />
          <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
            {progress.toFixed(2)}% of {maxValue}% limit
          </Typography>
        </Box>
      )}
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography sx={{ color: '#fff' }}>Loading summary...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#131722', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Impersonation Banner */}
        {localStorage.getItem('impersonationActive') === 'true' && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3, 
              bgcolor: 'rgba(245, 158, 11, 0.1)', 
              border: '1px solid #f59e0b',
              '& .MuiAlert-icon': {
                color: '#f59e0b'
              }
            }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Restore the original admin token and user data
                  const originalToken = localStorage.getItem('originalAdminToken');
                  const originalUser = localStorage.getItem('originalAdminUser');
                  
                  if (originalToken) {
                    localStorage.setItem('authToken', originalToken);
                    localStorage.removeItem('originalAdminToken');
                  }
                  
                  if (originalUser) {
                    localStorage.setItem('user', originalUser);
                    localStorage.removeItem('originalAdminUser');
                  }
                  
                  // Clear impersonation data
                  localStorage.removeItem('impersonationActive');
                  localStorage.removeItem('impersonatedUser');
                  localStorage.removeItem('impersonatedBy');
                  
                  // Redirect back to user management
                  window.location.href = '/usermanagement';
                }}
                sx={{ 
                  color: '#f59e0b', 
                  borderColor: '#f59e0b',
                  '&:hover': {
                    bgcolor: 'rgba(245, 158, 11, 0.1)',
                    borderColor: '#f59e0b'
                  }
                }}
              >
                Exit Impersonation
              </Button>
            }
          >
            <Typography sx={{ fontWeight: 600, color: '#fff' }}>
              🔴 Impersonation Mode Active
            </Typography>
            <Typography variant="body2" sx={{ color: '#d1d4dc' }}>
              Viewing as: <strong>{JSON.parse(localStorage.getItem('impersonatedUser') || '{}').email || 'Unknown'}</strong>
              {' | '}
              Impersonated by: <strong>{localStorage.getItem('impersonatedBy') || 'Unknown'}</strong>
            </Typography>
          </Alert>
        )}
        
        {/* Welcome Banner */}
        <Box 
          sx={{ 
            mb: 4, 
            p: 3, 
            bgcolor: '#1e222d',
            borderRadius: 2,
            border: '1px solid #2a2e39',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
              Welcome back, {user?.firstName || 'Trader'}! 👋
            </Typography>
            <Typography variant="body1" sx={{ color: '#10b981', fontStyle: 'italic', fontWeight: 500 }}>
              "Every trade is a lesson, every profit is progress. Stay disciplined, trade smart."
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RocketLaunchIcon />}
            onClick={() => setShowBuyModal(true)}
            sx={{
              bgcolor: '#4ade80',
              color: '#fff',
              fontWeight: 700,
              fontSize: '1rem',
              px: 4,
              py: 1.5,
              border: '2px solid #4ade80',
              boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
              '&:hover': {
                bgcolor: '#22c55e',
                borderColor: '#22c55e',
                color: '#fff',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(74, 222, 128, 0.5)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Buy Challenge
          </Button>
        </Box>

        {/* My Challenges Section with Comprehensive Progress */}
        {userChallenges.length > 0 && (
          <Box sx={{ mb: 4 }}>
            {/* Active Challenges Section */}
            {userChallenges.filter(c => !['Rejected', 'Expired', 'Cancelled', 'Failed'].includes(c.Status)).length > 0 && (
              <>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
                  📊 Active Challenges
                </Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {userChallenges
                    .filter(c => !['Rejected', 'Expired', 'Cancelled', 'Failed'].includes(c.Status))
                    .map((challenge) => {
                      const phaseBadge = getPhaseBadge(challenge.CurrentPhase);
                      
                      // Check if MT5 manual data exists and use it for MT5 platform challenges
                      const hasMT5Data = challenge.Platform === 'MT5' && challenge.MT5_ProfitTargetPercent !== null;
                      
                      // Use MT5 manual data if available, otherwise use challenge account summary
                      const currentProfit = hasMT5Data 
                        ? (challenge.MT5_ProfitTargetPercent || 0)
                        : (challenge.CurrentProfitPercent || 0);
                      
                      const targetPercent = challenge.CurrentPhaseTargetPercent || (challenge.CurrentPhase === 'Phase1' ? 10 : 5);
                      const profitProgress = Math.min((currentProfit / targetPercent) * 100, 100);
                      
                      const dailyDrawdown = hasMT5Data 
                        ? (challenge.MT5_DailyDrawdownPercent || 0) 
                        : (challenge.DailyDrawdownPercent || 0);
                      
                      const totalDrawdown = hasMT5Data 
                        ? (challenge.MT5_TotalDrawdownPercent || 0)
                        : (challenge.TotalDrawdownPercent || 0);
                      
                      const dailyDrawdownColor = getProgressColor(dailyDrawdown, 5);
                      const totalDrawdownColor = getProgressColor(totalDrawdown, 10);
                      
                      const isNearDailyLimit = dailyDrawdown >= 4;
                      const isNearTotalLimit = totalDrawdown >= 8;
                      const scalpingCount = challenge.ScalpingViolationsCount || 0;
                      
                      // Get current balance and total trades (prefer MT5 data if available)
                      const currentBalance = hasMT5Data 
                        ? (challenge.MT5_CurrentEquity || challenge.ChallengeAmount)
                        : (challenge.CurrentBalance || challenge.ChallengeAmount);
                      
                      const totalTrades = hasMT5Data 
                        ? (challenge.MT5_OrdersPlaced || 0)
                        : (challenge.TotalTrades || 0);
                
                return (
                  <Grid item xs={12} md={6} key={challenge.ChallengeID}>
                    <Card
                      sx={{
                        bgcolor: '#1e222d',
                        border: challenge.CurrentPhase === 'Failed' ? '1px solid #f44336' : '1px solid #2a2e39',
                        borderRadius: 2,
                        p: 3,
                        opacity: challenge.CurrentPhase === 'Failed' ? 0.7 : 1
                      }}
                    >
                      {/* Header with Icon, Name, and Phase Badge */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Typography variant="h4" sx={{ fontSize: '2rem' }}>
                            {challenge.ChallengeType === 'Pro' ? '🚀' : challenge.ChallengeType === 'Elite' ? '🏆' : '👑'}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {editingChallengeId === challenge.ChallengeID ? (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                  size="small"
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  fullWidth
                                  autoFocus
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      color: '#fff',
                                      fontSize: '14px',
                                      '& fieldset': { borderColor: '#2a2e39' },
                                      '&:hover fieldset': { borderColor: '#3b82f6' },
                                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                                    }
                                  }}
                                />
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleRenameChallenge(challenge.ChallengeID)}
                                  sx={{ color: '#10b981' }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  onClick={() => { setEditingChallengeId(null); setEditingName(''); }}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, fontSize: '16px' }}>
                                  {challenge.CustomName || `${challenge.ChallengeType} Challenge`}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => { 
                                    setEditingChallengeId(challenge.ChallengeID); 
                                    setEditingName(challenge.CustomName || `${challenge.ChallengeType} Challenge`);
                                  }}
                                  sx={{ color: '#888', '&:hover': { color: '#fff' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                              ${challenge.ChallengeAmount?.toLocaleString()} • {new Date(challenge.PurchaseDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#888', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                              <span style={{ fontSize: '10px' }}>🖥️</span> {challenge.Platform || 'MT5'} Platform
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Chip
                          label={phaseBadge.label}
                          sx={{
                            height: 24,
                            fontSize: '11px',
                            fontWeight: 700,
                            color: phaseBadge.color,
                            bgcolor: phaseBadge.bgColor,
                            border: `1px solid ${phaseBadge.color}`
                          }}
                        />
                      </Box>

                      {/* Warning Alerts */}
                      {(isNearDailyLimit || isNearTotalLimit || scalpingCount > 0 || (hasMT5Data && challenge.MT5_HasViolations)) && (
                        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {isNearDailyLimit && (
                            <Alert 
                              severity="warning" 
                              icon={<WarningIcon fontSize="small" />}
                              sx={{ 
                                py: 0.5, 
                                fontSize: '12px',
                                bgcolor: 'rgba(245, 158, 11, 0.1)',
                                color: '#f59e0b',
                                '& .MuiAlert-icon': { color: '#f59e0b' }
                              }}
                            >
                              Daily drawdown at {dailyDrawdown.toFixed(2)}% (limit: 5%)
                            </Alert>
                          )}
                          {isNearTotalLimit && (
                            <Alert 
                              severity="error" 
                              icon={<ErrorOutlineIcon fontSize="small" />}
                              sx={{ 
                                py: 0.5, 
                                fontSize: '12px',
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444',
                                '& .MuiAlert-icon': { color: '#ef4444' }
                              }}
                            >
                              Total drawdown at {totalDrawdown.toFixed(2)}% (limit: 10%)
                            </Alert>
                          )}
                          {hasMT5Data && challenge.MT5_HasViolations && challenge.MT5_ViolationReason && (
                            <Alert 
                              severity="error" 
                              icon={<ErrorOutlineIcon fontSize="small" />}
                              sx={{ 
                                py: 0.5, 
                                fontSize: '12px',
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444',
                                '& .MuiAlert-icon': { color: '#ef4444' }
                              }}
                            >
                              Violation: {challenge.MT5_ViolationReason}
                            </Alert>
                          )}
                          {scalpingCount > 0 && (
                            <Alert 
                              severity="info" 
                              icon={<TimerIcon fontSize="small" />}
                              sx={{ 
                                py: 0.5, 
                                fontSize: '12px',
                                bgcolor: 'rgba(59, 130, 246, 0.1)',
                                color: '#3b82f6',
                                '& .MuiAlert-icon': { color: '#3b82f6' }
                              }}
                            >
                              {scalpingCount} scalping violation{scalpingCount > 1 ? 's' : ''} detected
                            </Alert>
                          )}
                        </Box>
                      )}

                      {/* Profit Target Progress */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#888', fontWeight: 600 }}>
                            Profit Target ({challenge.CurrentPhase === 'Phase1' ? 'Phase 1' : challenge.CurrentPhase === 'Phase2' ? 'Phase 2' : 'Complete'})
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: currentProfit >= 0 ? '#10b981' : '#ef4444', 
                              fontWeight: 700 
                            }}
                          >
                            {currentProfit.toFixed(2)}% / {targetPercent}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={profitProgress}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            bgcolor: '#2a2e39',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: currentProfit >= targetPercent ? '#10b981' : '#3b82f6',
                              borderRadius: 1
                            }
                          }}
                        />
                        {challenge.CurrentPhase !== 'Failed' && challenge.CurrentPhase !== 'Funded' && (
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', mt: 0.5, display: 'block' }}>
                            {currentProfit >= targetPercent 
                              ? '🎉 Target achieved! Phase will advance automatically.' 
                              : `${(targetPercent - currentProfit).toFixed(2)}% remaining to advance`
                            }
                          </Typography>
                        )}
                      </Box>

                      {/* Drawdown Meters */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
                                Daily Drawdown
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ color: dailyDrawdownColor, fontWeight: 700, fontSize: '11px' }}
                              >
                                {dailyDrawdown.toFixed(2)}% / 5%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={(dailyDrawdown / 5) * 100}
                              sx={{
                                height: 6,
                                borderRadius: 1,
                                bgcolor: '#2a2e39',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: dailyDrawdownColor,
                                  borderRadius: 1
                                }
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
                                Total Drawdown
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ color: totalDrawdownColor, fontWeight: 700, fontSize: '11px' }}
                              >
                                {totalDrawdown.toFixed(2)}% / 10%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={(totalDrawdown / 10) * 100}
                              sx={{
                                height: 6,
                                borderRadius: 1,
                                bgcolor: '#2a2e39',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: totalDrawdownColor,
                                  borderRadius: 1
                                }
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Account Stats */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: 2, 
                        p: 2, 
                        bgcolor: '#131722', 
                        borderRadius: 1,
                        mb: 2
                      }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', display: 'block' }}>
                            Current Balance
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                            ${currentBalance?.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', display: 'block' }}>
                            Total Trades
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                            {totalTrades}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', display: 'block' }}>
                            Win Rate
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                            {challenge.WinRate?.toFixed(1) || 0}%
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* MT5 Data Source Indicator */}
                      {hasMT5Data && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          p: 1.5, 
                          bgcolor: 'rgba(59, 130, 246, 0.1)', 
                          borderRadius: 1,
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          mb: 2
                        }}>
                          <CheckCircleOutlineIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
                          <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, fontSize: '11px' }}>
                            Data updated by admin on {new Date(challenge.MT5_EntryDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}

                      {/* MT5 Credentials Section */}
                      {challenge.Platform === 'MT5' && (
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: '#131722', 
                          borderRadius: 1,
                          border: '1px solid #2a2e39',
                          mb: 2
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '10px' }}>🔐</Typography>
                            <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, fontSize: '11px' }}>
                              MT5 Platform Credentials
                            </Typography>
                          </Box>
                          
                          {!challenge.MT5Username && !challenge.MT5Password ? (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              p: 1.5, 
                              bgcolor: 'rgba(245, 158, 11, 0.1)', 
                              borderRadius: 1,
                              border: '1px solid rgba(245, 158, 11, 0.3)'
                            }}>
                              <WarningIcon sx={{ color: '#f59e0b', fontSize: 16 }} />
                              <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '11px' }}>
                                Admin has not set credentials yet. Please wait.
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {/* Username */}
                              <Box>
                                <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', display: 'block', mb: 0.5 }}>
                                  Username
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  p: 1,
                                  bgcolor: '#2a2e39',
                                  borderRadius: 1
                                }}>
                                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, fontSize: '12px', flex: 1 }}>
                                    {challenge.MT5Username || 'Not set'}
                                  </Typography>
                                  {challenge.MT5Username && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(challenge.MT5Username);
                                        // Could add a toast notification here
                                      }}
                                      sx={{ color: '#4ade80', p: 0.5 }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>

                              {/* Password */}
                              <Box>
                                <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', display: 'block', mb: 0.5 }}>
                                  Password
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  p: 1,
                                  bgcolor: '#2a2e39',
                                  borderRadius: 1
                                }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: '#fff', 
                                      fontWeight: 500, 
                                      fontSize: '12px', 
                                      flex: 1,
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    {challenge.MT5Password ? '••••••••' : 'Not set'}
                                  </Typography>
                                  {challenge.MT5Password && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(challenge.MT5Password);
                                        // Could add a toast notification here
                                      }}
                                      sx={{ color: '#4ade80', p: 0.5 }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>

                              {challenge.MT5CredentialsSetDate && (
                                <Typography variant="caption" sx={{ color: '#666', fontSize: '9px', fontStyle: 'italic' }}>
                                  Credentials set on {new Date(challenge.MT5CredentialsSetDate).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* MT5 Data Update Disclaimer */}
                      {challenge.Platform === 'MT5' && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          p: 1.5, 
                          bgcolor: 'rgba(245, 158, 11, 0.1)', 
                          borderRadius: 1,
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          mb: 2
                        }}>
                          <Typography variant="caption" sx={{ fontSize: '10px' }}>ℹ️</Typography>
                          <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '11px', fontStyle: 'italic' }}>
                            Data will be updated on a 24-hour basis
                          </Typography>
                        </Box>
                      )}

                      {/* Failure Reason */}
                      {challenge.CurrentPhase === 'Failed' && challenge.FailureReason && (
                        <Alert 
                          severity="error"
                          icon={<ErrorOutlineIcon />}
                          sx={{ 
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336',
                            '& .MuiAlert-icon': { color: '#f44336' }
                          }}
                        >
                          Challenge Failed: {challenge.FailureReason}
                        </Alert>
                      )}

                      {/* Success Message for Funded */}
                      {challenge.CurrentPhase === 'Funded' && (
                        <Alert 
                          severity="success"
                          icon={<CheckCircleOutlineIcon />}
                          sx={{ 
                            bgcolor: 'rgba(255, 215, 0, 0.1)',
                            color: '#ffd700',
                            '& .MuiAlert-icon': { color: '#ffd700' }
                          }}
                        >
                          🎉 Congratulations! Challenge complete. Contact support for funding.
                        </Alert>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
              </>
            )}

            {/* Inactive Challenges Section */}
            {userChallenges.filter(c => ['Rejected', 'Expired', 'Cancelled', 'Failed'].includes(c.Status)).length > 0 && (
              <>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#888', mb: 2, mt: 4 }}>
                  📜 Inactive Challenges (Past)
                </Typography>
                <Grid container spacing={3}>
                  {userChallenges
                    .filter(c => ['Rejected', 'Expired', 'Cancelled', 'Failed'].includes(c.Status))
                    .map((challenge) => (
                      <Grid item xs={12} md={6} key={challenge.ChallengeID}>
                        <Card
                          sx={{
                            bgcolor: '#1a1d26',
                            border: '1px solid #2a2e39',
                            borderRadius: 2,
                            p: 3,
                            opacity: 0.6
                          }}
                        >
                          {/* Header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h4" sx={{ fontSize: '2rem', filter: 'grayscale(100%)' }}>
                                {challenge.ChallengeType === 'Pro' ? '🚀' : challenge.ChallengeType === 'Elite' ? '🏆' : '👑'}
                              </Typography>
                              <Box>
                                <Typography sx={{ color: '#888', fontSize: 16, fontWeight: 600 }}>
                                  {challenge.CustomName || challenge.ChallengeType}
                                </Typography>
                                <Typography sx={{ color: '#666', fontSize: 13 }}>
                                  ${challenge.ChallengeAmount?.toLocaleString()} • {new Date(challenge.PurchaseDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={challenge.Status}
                              size="small"
                              sx={{
                                bgcolor: challenge.Status === 'Rejected' ? 'rgba(239, 68, 68, 0.2)' : 
                                         challenge.Status === 'Expired' ? 'rgba(245, 158, 11, 0.2)' :
                                         challenge.Status === 'Failed' ? 'rgba(239, 68, 68, 0.2)' : 
                                         'rgba(158, 158, 158, 0.2)',
                                color: challenge.Status === 'Rejected' ? '#ef4444' : 
                                       challenge.Status === 'Expired' ? '#f59e0b' :
                                       challenge.Status === 'Failed' ? '#ef4444' : 
                                       '#888',
                                fontWeight: 600,
                                fontSize: 11
                              }}
                            />
                          </Box>

                          {/* Failure Reason */}
                          {challenge.FailureReason && (
                            <Alert 
                              severity="error"
                              icon={<ErrorOutlineIcon />}
                              sx={{ 
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444',
                                fontSize: 13,
                                '& .MuiAlert-icon': { color: '#ef4444' }
                              }}
                            >
                              {challenge.FailureReason}
                            </Alert>
                          )}
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </>
            )}
          </Box>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
            Account Overview
          </Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>
            Real-time balance, equity, and profit/loss metrics
          </Typography>
        </Box>

        {/* Account Overview - Top 4 Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={AccountBalanceWalletIcon}
              title="Account Balance"
              value={`$${summary.accountBalance.toLocaleString()}`}
              subtitle="Starting Capital"
              color="#3b82f6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={ShowChartIcon}
              title="Current Equity"
              value={`$${summary.currentEquity.toLocaleString()}`}
              subtitle={`${summary.currentEquity >= summary.accountBalance ? '+' : ''}$${(summary.currentEquity - summary.accountBalance).toFixed(2)}`}
              color={summary.currentEquity >= summary.accountBalance ? '#10b981' : '#ef4444'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={summary.totalProfitLoss >= 0 ? TrendingUpIcon : TrendingDownIcon}
              title="Total P/L"
              value={`${summary.totalProfitLoss >= 0 ? '+' : ''}$${summary.totalProfitLoss.toFixed(2)}`}
              subtitle={`${summary.winRate}% Win Rate`}
              color={summary.totalProfitLoss >= 0 ? '#10b981' : '#ef4444'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={AssessmentIcon}
              title="Total Trades"
              value={summary.totalTrades}
              subtitle={`${summary.openPositions} Open, ${summary.pendingOrders} Pending`}
              color="#8b5cf6"
            />
          </Grid>
        </Grid>

        {/* Risk Management */}
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
          Risk Management
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <MetricCard
              icon={WarningIcon}
              title="Daily Drawdown"
              value={`${summary.dailyDrawdown.toFixed(2)}%`}
              subtitle="Maximum allowed: 5%"
              color={summary.dailyDrawdown > 4 ? '#ef4444' : '#fbbf24'}
              progress={summary.dailyDrawdown}
              maxValue={5}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MetricCard
              icon={WarningIcon}
              title="Total Drawdown"
              value={`${summary.totalDrawdown.toFixed(2)}%`}
              subtitle="Maximum allowed: 10%"
              color={summary.totalDrawdown > 8 ? '#ef4444' : '#fbbf24'}
              progress={summary.totalDrawdown}
              maxValue={10}
            />
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
          Performance Metrics
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={EmojiEventsIcon}
              title="Best Trade"
              value={`$${summary.bestTrade.toFixed(2)}`}
              color="#10b981"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={TrendingDownIcon}
              title="Worst Trade"
              value={`$${summary.worstTrade.toFixed(2)}`}
              color="#ef4444"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={ShowChartIcon}
              title="Win Rate"
              value={`${summary.winRate}%`}
              subtitle={`${summary.winningTrades}W / ${summary.losingTrades}L`}
              color="#3b82f6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={TimerIcon}
              title="Trading Days"
              value={summary.tradingDays}
              subtitle={`${(summary.totalTrades / Math.max(summary.tradingDays, 1)).toFixed(1)} trades/day`}
              color="#06b6d4"
            />
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
          Recent Closed Trades
        </Typography>
        <Card
          sx={{
            bgcolor: '#1e222d',
            border: '1px solid #2a2e39',
            borderRadius: 2,
            p: 3
          }}
        >
          {summary.recentTrades.length === 0 ? (
            <Typography sx={{ color: '#888', textAlign: 'center', py: 4 }}>
              No closed trades yet
            </Typography>
          ) : (
            <Box>
              {summary.recentTrades.map((trade, index) => (
                <Box key={trade.OrderID}>
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                        {trade.Symbol}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem', mb: 0.3 }}>
                        {trade.InternalOrderType} • {trade.Volume} lots • {trade.PipsGainLoss > 0 ? '+' : ''}{trade.PipsGainLoss} pips
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                        Entry: {trade.EntryPrice} → Exit: {trade.ExitPrice} • {Math.floor(trade.DurationMinutes / 60)}h {trade.DurationMinutes % 60}m
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 3, minWidth: '80px', maxWidth: '120px' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: trade.ActualProfitLoss >= 0 ? '#10b981' : '#ef4444',
                          fontWeight: 700,
                          fontSize: '1.05rem',
                          mb: 0.3,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {trade.ActualProfitLoss >= 0 ? '+' : ''}${parseFloat(trade.ActualProfitLoss).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        {new Date(trade.CloseTimestamp).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                      </Typography>
                    </Box>
                  </Box>
                  {index < summary.recentTrades.length - 1 && <Divider sx={{ borderColor: '#2a2e39' }} />}
                </Box>
              ))}
            </Box>
          )}
        </Card>

        {/* Buy Challenge Modal */}
        <Dialog 
          open={showBuyModal} 
          onClose={() => setShowBuyModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#131722',
              border: '1px solid #2a2e39',
              minHeight: '600px'
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: '#1e222d', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Choose Your Challenge
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: '#131722', p: 0 }}>
            <Box sx={{ display: 'flex', height: '500px' }}>
              {/* Left Panel - Challenge Selection */}
              <Box sx={{ width: '35%', borderRight: '1px solid #2a2e39', p: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {challenges.map((challenge) => (
                    <Card
                      key={challenge.type}
                      onClick={() => handleChallengeSelect(challenge)}
                      sx={{
                        bgcolor: selectedChallengeForDetails?.type === challenge.type ? '#1e222d' : '#131722',
                        border: `2px solid ${selectedChallengeForDetails?.type === challenge.type ? challenge.color : '#2a2e39'}`,
                        borderRadius: 2,
                        p: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: challenge.color,
                          transform: 'translateX(4px)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h3" sx={{ fontSize: '2rem' }}>
                          {challenge.icon}
                        </Typography>
                        <Box>
                          <Typography variant="body1" sx={{ color: challenge.color, fontWeight: 700, mb: 0.5 }}>
                            {challenge.type} Challenge
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                            ${challenge.amount.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>

              {/* Right Panel - Challenge Details */}
              <Box sx={{ width: '65%', p: 3, overflowY: 'auto' }}>
                {selectedChallengeForDetails ? (
                  <Box>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h4" sx={{ color: selectedChallengeForDetails.color, fontWeight: 700, mb: 1 }}>
                        {selectedChallengeForDetails.icon} {selectedChallengeForDetails.type} Challenge
                      </Typography>
                      <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
                        ${selectedChallengeForDetails.amount.toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Overview */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                        Overview
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888', lineHeight: 1.7 }}>
                        {selectedChallengeForDetails.overview}
                      </Typography>
                    </Box>

                    {/* Key Details */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                        Key Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {selectedChallengeForDetails.details.map((detail, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <CheckIcon sx={{ color: selectedChallengeForDetails.color, mr: 1.5, fontSize: 18, mt: 0.2 }} />
                            <Typography variant="body2" sx={{ color: '#d1d4dc', lineHeight: 1.6 }}>
                              <strong>{detail.label}:</strong> {detail.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Platform Selection */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#1a1d26', borderRadius: 2, border: '1px solid #2a2e39' }}>
                      <FormControl component="fieldset">
                        <FormLabel 
                          component="legend" 
                          sx={{ 
                            color: '#fff', 
                            fontWeight: 600, 
                            mb: 1.5,
                            '&.Mui-focused': { color: '#fff' }
                          }}
                        >
                          Select Trading Platform
                        </FormLabel>
                        <RadioGroup
                          value={selectedPlatform}
                          onChange={(e) => setSelectedPlatform(e.target.value)}
                          sx={{ gap: 1 }}
                        >
                          <FormControlLabel 
                            value="MT5" 
                            control={
                              <Radio 
                                sx={{ 
                                  color: '#888',
                                  '&.Mui-checked': { color: selectedChallengeForDetails.color }
                                }} 
                              />
                            } 
                            label={
                              <Typography sx={{ color: '#d1d4dc', fontWeight: 500 }}>
                                MT5 Platform
                              </Typography>
                            }
                          />
                          <FormControlLabel 
                            value="XFUNDING" 
                            control={
                              <Radio 
                                sx={{ 
                                  color: '#888',
                                  '&.Mui-checked': { color: selectedChallengeForDetails.color }
                                }} 
                              />
                            } 
                            label={
                              <Typography sx={{ color: '#d1d4dc', fontWeight: 500 }}>
                                XFUNDING Platform
                              </Typography>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    {/* Buy Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => handleBuyClick(selectedChallengeForDetails)}
                      sx={{
                        bgcolor: selectedChallengeForDetails.color,
                        color: '#fff',
                        fontWeight: 700,
                        py: 1.5,
                        fontSize: '1.1rem',
                        '&:hover': {
                          bgcolor: selectedChallengeForDetails.color,
                          opacity: 0.9
                        }
                      }}
                    >
                      Buy Now - ${selectedChallengeForDetails.accessFee}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body1" sx={{ color: '#888' }}>
                      ← Select a challenge to view details
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ bgcolor: '#1e222d', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
            <Button 
              onClick={() => {
                setShowBuyModal(false);
                setSelectedChallengeForDetails(null);
              }}
              sx={{ color: '#888', fontWeight: 600 }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transaction Details Modal */}
        <Dialog 
          open={showTransactionModal} 
          onClose={() => !purchaseSuccess && setShowTransactionModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#131722',
              border: '1px solid #2a2e39'
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: '#1e222d', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {purchaseSuccess ? 'Success!' : 'Complete Purchase'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: '#131722', py: 4 }}>
            {purchaseSuccess ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  ✅
                </Typography>
                <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 600, mb: 2 }}>
                  Purchase Recorded Successfully!
                </Typography>
                <Typography sx={{ color: '#888', lineHeight: 1.6 }}>
                  Support team will verify your transaction details and activate your challenge within 24 hours. 
                  You'll receive a confirmation email once activated.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography sx={{ color: '#888', mb: 2 }}>
                  After completing payment in the opened tab, please enter your transaction details below:
                </Typography>
                <TextField
                  label="Transaction ID"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ sx: { color: '#787b86' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: '#2a2e39' },
                      '&:hover fieldset': { borderColor: '#3b82f6' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
                <TextField
                  label="Email ID (used for payment)"
                  type="email"
                  value={paymentEmail}
                  onChange={(e) => setPaymentEmail(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ sx: { color: '#787b86' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: '#2a2e39' },
                      '&:hover fieldset': { borderColor: '#3b82f6' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
              </Box>
            )}
          </DialogContent>
          {!purchaseSuccess && (
            <DialogActions sx={{ bgcolor: '#1e222d', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
              <Button 
                onClick={() => setShowTransactionModal(false)}
                sx={{ color: '#888' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTransactionSubmit}
                variant="contained"
                sx={{
                  bgcolor: '#4ade80',
                  color: '#1e293b',
                  fontWeight: 700,
                  '&:hover': {
                    bgcolor: '#22c55e'
                  }
                }}
              >
                Submit
              </Button>
            </DialogActions>
          )}
        </Dialog>
      </Container>
    </Box>
  );
}

export default TraderSummary;
